import { createTheme } from '@mui/material/styles';
import { colors } from './colors.js';
import { typography } from './typography.js';
import { breakpoints } from './breakpoints.js';

const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
      light: colors.primaryLight,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.accent,
      light: colors.accentLight,
      dark: colors.accentDark,
      contrastText: colors.white,
    },
    success: { main: colors.success, contrastText: colors.white },
    warning: { main: colors.warning, contrastText: colors.white },
    error: { main: colors.error, contrastText: colors.white },
    info: { main: colors.info, contrastText: colors.white },
    background: {
      default: colors.background,
      paper: colors.surfaceSolid,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    divider: colors.divider,
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F7',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: colors.grey,
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 12,
    h1: typography.h1,
    h2: typography.h2,
    h3: typography.h3,
    h4: typography.h4,
    h5: typography.h5,
    h6: typography.h6,
    body1: typography.body1,
    body2: typography.body2,
    caption: typography.caption,
    button: {
      ...typography.button,
      textTransform: 'none',
    },
    overline: typography.overline,
  },
  breakpoints: {
    values: breakpoints,
  },
  shape: {
    borderRadius: 18,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*::-webkit-scrollbar': {
          width: '5px',
          height: '5px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          background: 'rgba(0,0,0,0.08)',
          borderRadius: '3px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(0,0,0,0.15)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 14px rgba(69, 39, 160, 0.25)',
          },
        },
        outlined: {
          borderColor: colors.glass.border,
          backdropFilter: colors.glass.blur,
          WebkitBackdropFilter: colors.glass.blur,
          backgroundColor: colors.glass.background,
          '&:hover': {
            borderColor: colors.glass.borderHover,
            backgroundColor: colors.glass.backgroundHover,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          boxShadow: colors.shadow,
          border: `1px solid ${colors.glass.border}`,
          backgroundColor: colors.glass.background,
          backdropFilter: colors.glass.blur,
          WebkitBackdropFilter: colors.glass.blur,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 14,
            backgroundColor: colors.glass.background,
            backdropFilter: colors.glass.blur,
            WebkitBackdropFilter: colors.glass.blur,
            '& fieldset': {
              borderColor: colors.glass.border,
            },
            '&:hover fieldset': {
              borderColor: colors.glass.borderHover,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 28,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.glass.border}`,
          backgroundColor: colors.glass.background,
          backdropFilter: colors.glass.blur,
          WebkitBackdropFilter: colors.glass.blur,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderTop: `1px solid ${colors.glass.border}`,
          backgroundColor: colors.glass.background,
          backdropFilter: colors.glass.blur,
          WebkitBackdropFilter: colors.glass.blur,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 22,
          boxShadow: colors.shadowDark,
          border: `1px solid ${colors.glass.border}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 10,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
  },
});

export default theme;
